import { useEffect, useState, useCallback, useRef } from '@wordpress/element';
import { getPreloaded } from '../utils/preload';

const MEMORY_CACHE = window.__vayuTemplateCache__ ||= {};
const TTL = 60 * 60 * 1000;
const MAX_CACHED_TEMPLATES = 10;
const MAX_TYPES_CACHED = 5;

const tryStorage = () => {
  try {
    localStorage.setItem('__test__', '__test__');
    localStorage.removeItem('__test__');
    return localStorage;
  } catch (e1) {
    try {
      sessionStorage.setItem('__test__', '__test__');
      sessionStorage.removeItem('__test__');
      return sessionStorage;
    } catch (e2) {
      return null;
    }
  }
};

const storage = tryStorage();

export const clearInMemoryCache = () => {
  window.__vayuTemplateCache__ = {};
};

const updateLRU = (type) => {
  if (!storage) return;
  let lru = [];
  try {
    lru = JSON.parse(storage.getItem('vayuCache_LRU') || '[]');
  } catch (_) {}

  const updated = [type, ...lru.filter((t) => t !== type)];
  if (updated.length > MAX_TYPES_CACHED) {
    const evicted = updated.pop();
    storage.removeItem(`vayuCache_${evicted}`);
  }

  storage.setItem('vayuCache_LRU', JSON.stringify(updated));
};

const safeGetLocal = (key) => {
  if (!storage) return null;
  try {
    const data = JSON.parse(storage.getItem(key));
    if (data && Date.now() - data.timestamp < TTL) {
      return data;
    }
  } catch (_) {}
  return null;
};

const safeSetLocal = (key, data) => {
  if (!storage) return;
  const limited = {
    templates: data.templates.slice(0, MAX_CACHED_TEMPLATES),
    total: data.total,
    timestamp: Date.now(),
  };
  try {
    storage.setItem(key, JSON.stringify(limited));
    const type = key.replace('vayuCache_', '');
    updateLRU(type);
  } catch (e) {
    console.warn(`[vayu] Failed to cache "${key}":`, e);
  }
};

const limitCacheTemplates = (data) => ({
  ...data,
  templates: data.templates.slice(0, MAX_CACHED_TEMPLATES),
});

const useTemplateManifest = (
  activeTab = 'patterns',
  overridePerPage = null,
  searchQuery = '',
  onError = () => {}
) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  const perPage = overridePerPage || 10;
  const cache = useRef(MEMORY_CACHE);
  const requestIdRef = useRef(0);
  const trimmedSearch = typeof searchQuery === 'string' ? searchQuery.trim() : '';
  const getKey = () => `${activeTab}__${trimmedSearch.toLowerCase()}`;
  const localKey = `vayuCache_${activeTab}`;

  const fetchTemplatesFromAPI = async (key, reset = false, isBackground = false) => {
    const currentRequestId = ++requestIdRef.current;
    try {
      if (!isBackground && reset && templates.length === 0) setLoading(true);
      else if (isBackground) setBackgroundLoading(true);

      const apiBase = window?.wpApiSettings?.root || '/wp-json/';
      const url = new URL(`${apiBase}vayu-blocks/v1/manifest`);
      url.searchParams.set('page', reset ? 1 : page);
      url.searchParams.set('per_page', perPage);
      url.searchParams.set('type', activeTab);
      if (trimmedSearch) url.searchParams.set('search', trimmedSearch);

      const res = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'X-WP-Nonce': window?.wpApiSettings?.nonce || '' },
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Fetch failed');
      const json = await res.json();
      if (currentRequestId !== requestIdRef.current) return;

      const normalized = json.templates.map((tpl) => ({
        ...tpl,
        type: tpl.type?.toLowerCase() || activeTab,
        favorite: tpl.favorite || false,
        premium: tpl.premium || 'free',
      }));

      const existing = reset ? [] : (cache.current[key]?.templates || []);
      const merged = [...existing, ...normalized];
      const unique = Array.from(new Map(merged.map(t => [t.id, t])).values());

      const final = {
        templates: unique,
        total: json.total,
        timestamp: Date.now(),
      };

      cache.current[key] = limitCacheTemplates(final);
      safeSetLocal(localKey, final);

      setTemplates(unique);
      setTotal(json.total || unique.length);
      setHasMore(unique.length < (json.total || 0));
    } catch (err) {
      console.error('Template load error:', err);
      onError('Could not load templates.');
    } finally {
      if (!isBackground) setLoading(false);
      if (isBackground) setBackgroundLoading(false);
    }
  };

  const fetchTemplates = useCallback(async (reset = false) => {
    const key = getKey();
    const local = safeGetLocal(localKey);

    if (
      reset &&
      trimmedSearch === '' &&
      getPreloaded(activeTab).length > 0 &&
      (!local || Date.now() - local.timestamp > TTL)
    ) {
      const pre = {
        templates: getPreloaded(activeTab).map(t => ({ ...t })),
        total: local?.total || getPreloaded(activeTab).length,
        timestamp: Date.now(),
      };

      cache.current[key] = limitCacheTemplates(pre);
      safeSetLocal(localKey, pre);
      setTemplates(pre.templates);
      setTotal(pre.total);
      setHasMore(pre.templates.length >= perPage);
      setLoading(false);
      return;
    }

    if (reset && trimmedSearch === '' && local) {
      cache.current[key] = local;
      setTemplates(local.templates);
      setTotal(local.total);
      setHasMore(local.templates.length < local.total);
      setLoading(false);

      if (Date.now() - local.timestamp > TTL) {
        fetchTemplatesFromAPI(key, true, true);
      }
      return;
    }

    if (reset && cache.current[key]) {
      const c = cache.current[key];
      setTemplates(c.templates);
      setTotal(c.total);
      setHasMore(c.templates.length < c.total);
      setLoading(false);

      if (!c.timestamp || Date.now() - c.timestamp > TTL) {
        fetchTemplatesFromAPI(key, true, true);
      }
      return;
    }

    await fetchTemplatesFromAPI(key, reset);
  }, [activeTab, trimmedSearch, perPage, page]);

  useEffect(() => {
    setPage(1);
    fetchTemplates(true);
  }, [activeTab, trimmedSearch]);

  useEffect(() => {
    if (page > 1) fetchTemplates();
  }, [page]);

  const loadMore = () => {
    if (!loading && hasMore) {
      setLoading(true);
      setPage((prev) => prev + 1);
    }
  };

  return {
    templates,
    loading,
    hasMore,
    loadMore,
    total,
  };
};

export default useTemplateManifest;
